import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userApi } from "@bw/shared/api";
import type { UserProfileResponse } from "@bw/shared/types/generated";
import { profileQueryKey } from "../../../common/query/useMyProfile";
import type { ProfileCreateBody } from "../schemas";

/**
 * useCreateProfile (player Slice 2).
 *
 * A React Query mutation over userApi.createProfile (POST /users/profile). On
 * success it:
 *   - returns the UserProfileResponse (so the page branches on account_status)
 *   - invalidates `profileQueryKey` so the next getMyProfile read is fresh and
 *     RequireProfile lets the user into the app.
 *
 * It surfaces the raw ApiError (409 ProfileAlreadyExists, 422 validation) on
 * `mutation.error` without throwing to an error boundary — the page renders the
 * message inline. No business logic here: the body is exactly UserProfileCreate.
 *
 * Types come from `@bw/shared/types/generated` (ADR-005): the real contract,
 * where `account_status` / `requires_parental_consent` are REQUIRED. This is the
 * single source the onboarding flow branches on for the COPPA age outcome.
 */
export function useCreateProfile() {
  const queryClient = useQueryClient();

  return useMutation<UserProfileResponse, unknown, ProfileCreateBody>({
    // `userApi.createProfile` is now typed against the GENERATED
    // UserProfileResponse (#1311) — account_status / requires_parental_consent
    // are REQUIRED at the boundary, so the page can branch on the COPPA age
    // outcome with no narrowing cast.
    mutationFn: (data: ProfileCreateBody) => userApi.createProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileQueryKey });
    },
  });
}
